import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useMotionValueEvent, useReducedMotion } from 'framer-motion';
import { Menu, X, LayoutDashboard, LogOut } from 'lucide-react';
import { useAuth } from '../../../auth/AuthProviderUnified';

// Defined at module scope so the component type is stable across renders
// (defining inside the component would remount the link each render).
const MotionLink = motion(Link);
const NAV_EASE = [0.22, 1, 0.36, 1];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const reduce = useReducedMotion();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, 'change', (latest) => {
    setIsScrolled(latest > 24);
  });

  const navLinks = [
    { name: 'Contractors', path: '/solutions/contractors' },
    { name: 'Insurers', path: '/solutions/insurers' },
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/80 backdrop-blur-md border-b border-black/10 py-3'
            : 'bg-transparent py-5 border-b border-transparent'
        }`}
      >
        <div className="max-w-[1120px] mx-auto px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center group z-50">
            <img
              src="/vh-intelligence-ink.png"
              alt="VH Intelligence"
              className="h-7 sm:h-8 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className="px-4 py-2 text-[14px] font-medium text-slate-600 hover:text-[#1C1C1E] transition-colors"
              >
                {item.name}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-4">
            {user ? (
              // Authenticated user: show Dashboard and Logout
              <>
                <Link
                  to="/command-center"
                  className="flex items-center gap-2 text-[14px] font-medium text-slate-600 hover:text-[#1C1C1E] transition-colors"
                >
                  <LayoutDashboard size={16} />
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 text-[14px] font-medium text-slate-600 hover:text-[#1C1C1E] transition-colors"
                >
                  <LogOut size={16} />
                  Log out
                </button>
              </>
            ) : (
              // Unauthenticated user: show Login and Get Started
              <>
                <Link
                  to="/login"
                  className="text-[14px] font-medium text-slate-600 hover:text-[#1C1C1E] transition-colors"
                >
                  Log in
                </Link>
                <MotionLink
                  to="/signup"
                  whileHover={reduce ? undefined : { scale: 1.02, y: -1 }}
                  whileTap={reduce ? undefined : { scale: 0.98 }}
                  transition={{ duration: 0.2, ease: NAV_EASE }}
                  className="group relative px-4 py-2 bg-[#1C1C1E] hover:bg-[#2C2C2E] text-white rounded-full text-[14px] font-medium transition-all duration-200 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-orange-600/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="relative">Get Started</span>
                </MotionLink>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-[#1C1C1E] p-2 hover:bg-black/5 rounded-lg transition-colors"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden fixed top-[60px] left-0 right-0 bg-white border-b border-black/10 z-40 overflow-hidden"
          >
            <div className="p-6 space-y-4">
              {navLinks.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-base font-medium text-slate-600 hover:text-[#1C1C1E] py-2"
                >
                  {item.name}
                </Link>
              ))}
              <div className="pt-4 border-t border-black/10 flex flex-col gap-3">
                {user ? (
                  // Authenticated user: show Dashboard and Logout
                  <>
                    <Link
                      to="/command-center"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center justify-center gap-2 text-center text-slate-600 hover:text-[#1C1C1E] py-2 font-medium"
                    >
                      <LayoutDashboard size={16} />
                      Dashboard
                    </Link>
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        handleLogout();
                      }}
                      className="flex items-center justify-center gap-2 text-center bg-[#1C1C1E] text-white font-medium py-3 rounded-full hover:bg-[#2C2C2E] transition-colors"
                    >
                      <LogOut size={16} />
                      Log out
                    </button>
                  </>
                ) : (
                  // Unauthenticated user: show Login and Get Started
                  <>
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block text-center text-slate-600 hover:text-[#1C1C1E] py-2 font-medium"
                    >
                      Log in
                    </Link>
                    <Link
                      to="/signup"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block text-center bg-[#1C1C1E] text-white font-bold py-3 rounded-full hover:bg-[#2C2C2E] transition-colors"
                    >
                      Get Started
                    </Link>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
